<script setup lang="ts">
import { computed } from 'vue'

import Badge from '@satch-ui/components/badges/Badge.vue'
import Status from '@satch-ui/components/status/Status.vue'
import { t } from '@satch-ui/translations/translations'
import { localizeUrl } from '@shared/fondof-fn/url/localize'
import AddToWishlistButton from '@storefront-satch/components/molecules/product/AddToWishlistButton.vue'
// delete single TrashButton component?
import TrashButton from '@storefront-satch/components/molecules/product/TrashButton.vue'
import type { ProductCardProps } from '@storefront-satch/components/molecules/product/card/types'
import { useWishlistItem } from '@storefront-satch/composables/wishlist'

import { vTracking } from '@fond-of/analytics'
import { vE2eTestId } from '@fond-of/telemetry'

const props = withDefaults(defineProps<ProductCardProps>(), {
  openInNewTab: false,
  showWishlistRemoveButton: false,
  showWishlistAddButton: false,
  buttonVariant: 'secondary',
  alignment: 'vertical',
  hideEdition: false,
  // needs to be refactored to sth like style
  variant: 'black',
})

const firstVariant = computed(() => {
  return props.product.variants[0]
})
const variantId = computed(() => firstVariant.value.id.toString())
const { removeFromWishlistItem } = useWishlistItem(variantId.value)

const isHorizontal = computed(() => {
  return props.alignment === 'horizontal'
})
</script>

<template>
  <div
    :class="['relative', props.class]"
    :product-id="props.product.id"
  >
    <TrashButton
      v-if="props.showWishlistRemoveButton && props.product.id"
      v-tracking="'cs-popin-remove-from-wishlist-layer'"
      v-e2e-test-id="'removeFromWishlistButton'"
      :data-sku="props.product.id"
      @click.stop="removeFromWishlistItem()"
    />

    <AddToWishlistButton
      v-if="props.showWishlistAddButton && props.product.id"
      :product-id="props.product.id.toString()"
      @click.stop
    />
    <a
      v-if="product"
      data-name="product card link"
      :class="[
        'flex flex-col font-sans',
        {
          'sm:flex-row sm:items-center': isHorizontal,
        },
      ]"
      :href="localizeUrl(`${props.product.url}`)"
      :target="openInNewTab ? '_blank' : '_self'"
    >
      <div
        :class="[
          'relative aspect-square w-full',
          { 'sm:w-[85%]': isHorizontal },
        ]"
      >
        <slot name="image" />

        <Badge
          v-if="firstVariant.isOffer"
          variant="discount"
        >
          {{
            firstVariant.discount === 100
              ? t('product.free')
              : t('product.sale')
          }}
        </Badge>
        <Badge
          v-else-if="props.product.isNew"
          variant="new"
          :class="{
            'text-white': props.variant === 'white',
          }"
        >
          {{ t('new') }}
        </Badge>

        <Status
          v-if="props.product.showEdition && !props.hideEdition"
          variant="limitedEdition"
          position="rotated"
          :padding="null"
          :class="{
            'text-white': props.variant === 'white',
          }"
        >
          {{ props.product.attributes.edition?.[0] }}
        </Status>
      </div>

      <slot
        name="content"
        :price="firstVariant.priceFormatted"
        :original-price="firstVariant.originalPriceFormatted"
        :currency-code="firstVariant.currencyCode"
        :is-offer="firstVariant.isOffer"
        :show-product-link="props.showProductLink"
        :button-variant="props.buttonVariant"
        :is-horizontal="isHorizontal"
        :show-edition="props.product.showEdition"
      />
    </a>
  </div>
</template>
